import * as React from 'react';

import GameTile, {
  AnchorLayer,
  JackpotLayerGameTile,
  LayersWrapper,
  LiveTableLayer,
  SashLayer,
  QuicksilverTileOutfitType,
} from '../../game-tile';

import GhostTile from '../GhostTile';
import { Info } from '../../icons';
import { GameTileProps, GameClickProps } from '../types';
import { CallbackMode } from '../../../logic/main/types';
import styles from './ModularGridGameTile.module.scss';
import cx from 'clsx';
import AssetLayer from '../../AssetLayer/AssetLayer';
import { INFO_LABEL, SMALL_OUTFIT } from '../constants';

interface ModularGridGameTileProps {
  gameDetailsInfo?: boolean;
  game?: GameTileProps;
  onClick?: (game: GameClickProps) => void;
  onInfoClick?: (game: GameClickProps, path: string) => void;
  outfit: QuicksilverTileOutfitType;
  isLazy?: boolean;
  isAnchor?: boolean;
  ghost?: boolean;
  callbackMode: CallbackMode;
  tileSize?: 'small' | 'large';
}

const ModularGridGameTile: React.FC<ModularGridGameTileProps> = ({
  game,
  onClick,
  onInfoClick,
  outfit,
  isLazy = false,
  isAnchor = false,
  ghost,
  gameDetailsInfo,
  callbackMode,
  tileSize = 'small',
}) => {
  if (ghost) {
    return (
      <div className={styles.ghostTileContainer}>
        <GhostTile />
      </div>
    );
  }

  if (game) {
    const gameClickProps = {
      id: game.id,
      title: game.title,
      name: game.name,
      provider: game.provider,
      integration: game.integration,
      presentation_code: game.presentation_code,
      transaction_code: game.transaction_code,
      launchUrl: game.launchUrl,
      gameDetailsPath: game.gameDetailsPath,
      gameLaunchPath: game.gameLaunchPath,
      type: game.type,
      trackingTitle: game.tracking_metadata?.title || game.title,
      sash: game.sash,
    };

    return (
      <div
        className={styles.container}
        data-testid="game-tile-wrapper"
        onClick={() => !isAnchor && onClick && onClick(gameClickProps)}
      >
        <GameTile data-testid="game-tile">
          <LayersWrapper rounded>
            <AssetLayer
              type="image"
              sizedSources={
                game.sizedImages &&
                game.sizedImages[
                  tileSize === 'small' ? 'smallTile' : 'largeTile'
                ]
              }
              loadingAnimation={game.loading}
              data-testid="picture-layer"
              imageSrc={game.image?.source}
              alt={game.image?.alt || ''}
              isLazy={isLazy}
            />
            {game.table && (
              <LiveTableLayer table={game.table} outfit={outfit} />
            )}
            {game.jackpot && !game.table && outfit !== SMALL_OUTFIT && (
              <JackpotLayerGameTile
                value={game.jackpot.value}
                currencySymbol={game.jackpot.currencySymbol}
              />
            )}
          </LayersWrapper>
          {isAnchor && game.launchUrl && (
            <AnchorLayer
              url={game.launchUrl}
              target="_blank"
              onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                if (callbackMode === 'event') e.preventDefault();
                onClick && onClick(gameClickProps);
              }}
              notIndexed={game.notIndexed}
            />
          )}
          {gameDetailsInfo && (
            <a
              className={cx(styles.info, styles.link)}
              target="_blank"
              rel="noopener noreferrer"
              href={game.gameDetailsPath}
              title={`Play ${game.title}`}
            >
              <div
                className={cx(
                  styles.infoWrapper,
                  tileSize === 'large' && styles.infoWrapperWithLabel
                )}
                onClick={e => {
                  e.preventDefault();
                  onInfoClick &&
                    onInfoClick(gameClickProps, game.gameDetailsPath);
                }}
              >
                <Info altText={`Play ${game.title}`} />
                {tileSize === 'large' && (
                  <p className={styles.infoLabel}>{INFO_LABEL}</p>
                )}
              </div>
            </a>
          )}
          {game.sash && (
            <SashLayer
              sash={game.sash.title}
              outfit={outfit}
              background={game.sash.background}
            />
          )}
        </GameTile>
      </div>
    );
  }
  return null;
};
export default ModularGridGameTile;
